import React from "react"
import { Autocomplete, TextField, SxProps } from "@mui/material"
import { IconArrowDown } from "../../../icons/IconArrowDown"

type OptionType = {
  label: string
  value: string | number
}

type AutocompleteInputProps = {
  options: OptionType[]
  label?: React.ReactNode
  placeholder?: string
  fullWidth?: boolean
  sx?: SxProps
  size?: "small" | "medium"
  freeSolo?: boolean
  disableClearable?: boolean
  multiple?: boolean

  value?: any
  onChange?: (value: any) => void
  onBlur?: () => void
  name?: string
  error?: boolean
  helperText?: React.ReactNode
  disabled?: boolean
}

function resolveSingleValue(
  options: OptionType[],
  value: unknown,
  freeSolo: boolean,
): OptionType | string | null {
  if (value === null || value === undefined || value === "") {
    return null
  }
  const matched = options.find((o) => o.value === value)
  if (matched) return matched
  if (freeSolo && (typeof value === "string" || typeof value === "number")) {
    return String(value)
  }
  return null
}

function resolveMultipleValue(options: OptionType[], value: unknown): OptionType[] {
  if (!Array.isArray(value)) {
    return []
  }
  return value
    .map((item) => options.find((o) => o.value === item))
    .filter((o): o is OptionType => Boolean(o))
}

export const AutocompleteInput = React.forwardRef<HTMLInputElement, AutocompleteInputProps>(
  (
    {
      options,
      label,
      placeholder = "Выберите значение",
      fullWidth = true,
      sx,
      value,
      onChange,
      onBlur,
      name,
      size = "medium",
      freeSolo = false,
      disableClearable = false,
      multiple = false,
      error,
      helperText,
      disabled = false,
    },
    ref
  ) => {
    if (multiple) {
      const resolvedValue = resolveMultipleValue(options, value ?? [])

      return (
        <Autocomplete
          multiple
          disableCloseOnSelect
          disabled={disabled}
          disableClearable={disableClearable}
          size={size}
          options={options}
          noOptionsText="Ничего не найдено"
          value={resolvedValue}
          onChange={(_, newValue) => {
            onChange?.(newValue.map((item) => item.value))
          }}
          onBlur={onBlur}
          sx={sx}
          popupIcon={<IconArrowDown />}
          fullWidth={fullWidth}
          getOptionLabel={(option) => option.label || ""}
          isOptionEqualToValue={(option, selected) => option.value === selected.value}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={resolvedValue.length === 0 ? placeholder : undefined}
              name={name}
              inputRef={ref}
              error={Boolean(error)}
              helperText={helperText}
              InputLabelProps={
                label
                  ? {
                      ...params.InputLabelProps,
                      shrink: resolvedValue.length > 0 || Boolean(params.inputProps?.value),
                    }
                  : params.InputLabelProps
              }
              sx={{ ...sx }}
            />
          )}
        />
      )
    }

    const resolvedValue = resolveSingleValue(options, value ?? "", freeSolo)

    return (
      <Autocomplete
        disabled={disabled}
        disableClearable={disableClearable}
        freeSolo={freeSolo}
        size={size}
        options={options}
        noOptionsText="Ничего не найдено"
        value={resolvedValue}
        onChange={(_, newValue) => {
          if (newValue == null) {
            onChange?.("")
            return
          }
          const val = typeof newValue === "string" ? newValue : newValue.value
          onChange?.(val ?? "")
        }}
        onInputChange={(_, inputValue, reason) => {
          if (!freeSolo) return
          if (reason === "input" || reason === "clear") {
            onChange?.(inputValue)
          }
        }}
        onBlur={onBlur}
        sx={sx}
        popupIcon={<IconArrowDown />}
        fullWidth={fullWidth}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.label || ""
        }
        isOptionEqualToValue={(option, selected) => {
          if (typeof selected === "string") {
            return option.value === selected || option.label === selected
          }
          return option.value === selected.value
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            name={name}
            inputRef={ref}
            error={Boolean(error)}
            helperText={helperText}
            InputLabelProps={
              label
                ? {
                    ...params.InputLabelProps,
                    shrink: Boolean(resolvedValue),
                  }
                : params.InputLabelProps
            }
            sx={{ ...sx }}
          />
        )}
      />
    )
  }
)

AutocompleteInput.displayName = "AutocompleteInput"
